import axios from 'axios'

import type { DocumentNode } from 'graphql'

/**
 * Extract query string from DocumentNode or return string as-is
 */
const getQueryString = (query: string | DocumentNode): string => {
  if (typeof query === 'string') {
    return query
  }
  // DocumentNode from .graphql files
  const source = query.loc?.source.body
  if (!source) {
    throw new Error('Invalid GraphQL query: missing source')
  }
  return source
}

/**
 * GraphQL request helper
 */
export const request = <TData = unknown>(
  query: string | DocumentNode,
  variables: Record<string, unknown> = {}
): Promise<{ data: TData }> => {
  return axios
    .post('/graphql', { query: getQueryString(query), variables })
    .then((response) => response.data as { data: TData })
}

/**
 * Admin controller request helper (with CSRF form_key)
 */
export const adminRequest = async <TData = unknown>(
  url: string,
  data: Record<string, unknown> = {}
): Promise<TData> => {
  const formKey = window.FORM_KEY
  if (!formKey) {
    throw new Error('Form key not available')
  }

  const formData = new FormData()
  formData.append('form_key', formKey)

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) {
      continue
    }
    if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value))
    } else {
      // eslint-disable-next-line @typescript-eslint/no-base-to-string -- value is primitive here
      formData.append(key, String(value))
    }
  }

  const response = await axios.post(url, formData, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })

  return response.data as TData
}

/**
 * React API dispatch helper
 *
 * Posts to the single dispatch endpoint with an action identifier.
 * Handles both JSON data and file uploads (FormData).
 */
export const adminDispatch = async <TData = unknown>(
  dispatchUrl: string,
  action: string,
  data: Record<string, unknown> | FormData = {}
): Promise<TData> => {
  const formKey = window.FORM_KEY
  if (!formKey) {
    throw new Error('Form key not available')
  }

  let formData: FormData

  if (data instanceof FormData) {
    formData = data
    formData.set('form_key', formKey)
    formData.set('action', action)
  } else {
    formData = new FormData()
    formData.append('form_key', formKey)
    formData.append('action', action)

    for (const [key, value] of Object.entries(data)) {
      if (value === null || value === undefined) {
        continue
      }
      if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value))
      } else {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string -- value is primitive here
        formData.append(key, String(value))
      }
    }
  }

  const response = await axios.post(dispatchUrl, formData, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })

  return response.data as TData
}
